import { Button } from "./button";
import { Icon, Lock, RotateCcw } from "./icon";

/**
 * Frozen-map read-only state (M7 Flow D). When an OWNER opens a map that was
 * frozen by a downgrade, the editor shows this read-only banner with a one-click
 * Resubscribe prompt that goes to checkout and unfreezes ALL their maps on
 * return. Freeze-not-delete: the copy is explicit that nothing was deleted.
 * Token-only; brand-free (no literals). The public path already 404s a frozen
 * map (Phase 2) — this is only the owner's in-editor state.
 */

export interface FrozenMapNoticeProps {
  /** Title of the frozen map, shown for orientation when known. */
  mapTitle?: string;
  /** Fires the resubscribe checkout redirect. */
  onResubscribe: () => void;
  /** Redirect is in flight. */
  resubscribing?: boolean;
}

export function FrozenMapNotice({ mapTitle, onResubscribe, resubscribing = false }: FrozenMapNoticeProps) {
  return (
    <section className="pz-frozen" role="status" aria-live="polite">
      <span className="pz-frozen__glyph" aria-hidden>
        <Icon icon={Lock} size="lg" />
      </span>
      <div className="pz-frozen__text">
        <h2 className="pz-frozen__heading">
          {mapTitle ? `“${mapTitle}” is frozen` : "This map is frozen"}
        </h2>
        <p className="pz-frozen__badge">Read-only</p>
        <p className="pz-frozen__copy">
          This map is over the limit of your current plan, so it is read-only and its share link and embeds are paused.
          Nothing has been deleted — resubscribe to unfreeze all your maps instantly.
        </p>
      </div>
      <Button
        variant="primary"
        size="lg"
        loading={resubscribing}
        onClick={onResubscribe}
        className="pz-frozen__cta"
        iconLeading={resubscribing ? undefined : <Icon icon={RotateCcw} size="sm" />}
      >
        Resubscribe
      </Button>
    </section>
  );
}
